<!-- @format -->

<template>
    <div
        class="gst-add-hotel-reservation-header d-flex flex-column"
        :class="{
            'gst-add-hotel-reservation-header--show-filter': showFilter,
        }">
        <div
            v-if="readOnly"
            class="d-flex align-center px-2 py-2">
            <div class="d-flex align-end">
                <CalendarIcon class="calendar-icon--read-only" />
                <div>{{ date }}</div>
            </div>
            <div class="dot"></div>
            <div>{{ hotelReservationQuantity }}</div>
        </div>
        <div
            v-else-if="!showFilter"
            class="gst-add-hotel-reservation-header__content d-flex flex-row px-2 py-2"
            :class="{ 'gst-add-hotel-reservation-header__content--disabled-filter': !allowUpdate }"
            tabindex="0"
            @click="allowUpdate && $emit('change-show-filter', true)"
            @keypress.enter="allowUpdate && $emit('change-show-filter', true)">
            <div class="ml-2">
                <div class="gst-add-hotel-reservation-header__wrapper d-flex">
                    <BaseIcon
                        symbol-id="icons--location"
                        width="18"
                        height="18"
                        class="mr-2 flex-shrink-0" />
                    <h6 class="gst-add-hotel-reservation-header__location-title">
                        {{ searchTitle }}
                    </h6>
                </div>
                <div class="gst-add-hotel-reservation-header__location-details">
                    <span>{{ date }}</span>
                    <span class="ml-2">{{ hotelReservationQuantity }}</span>
                </div>
            </div>
            <BaseIcon
                v-if="allowUpdate"
                class="gst-add-hotel-reservation-header__icon ml-auto mr-3 flex-shrink-0"
                symbol-id="icons--draw_on_map" />
        </div>
        <div
            v-if="!showFilter"
            class="gst-add-hotel-reservation-header__redeem d-flex items-center justify-end pa-2">
            <span class="gst-add-hotel-reservation-header__redeem-label">{{
                $t('redeemPoints.label')
            }}</span>
            <v-switch
                hide-details
                :input-value="redeemPointsToggle"
                class="gst-add-hotel-reservation-header__redeem-switch gst-switch pa-0 ma-0"
                @change="updateRedeemPointsToggle" />
            <BaseTooltip
                :text="
                    $t('redeemPoints.tooltip', {
                        count: minimumRedeemPoints,
                        formattedCount: formattedPoints,
                    })
                "
                :open-on-click="!$vuetify.breakpoint.mdAndUp"
                :open-on-hover="$vuetify.breakpoint.mdAndUp">
                <BaseIcon
                    symbol-id="icons--help"
                    :width="20"
                    :height="20" />
            </BaseTooltip>
        </div>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dateConstants from '@core/utils/constants/date';
import CalendarIcon from '@tenant/app/assets/icons/calendar.svg';
import datePeriod from '@tenant/app/filters/datePeriod';

export default {
    name: 'AddHotelReservationHeader',
    components: {
        CalendarIcon,
    },
    i18nOptions: {
        namespaces: 'main',
        keyPrefix: 'modules.hotel.features.addHotelReservation._components.header',
    },
    props: {
        startDateTime: {
            type: Date,
            required: true,
        },
        endDateTime: {
            type: Date,
            required: true,
        },
        roomsCount: {
            type: Number,
            required: true,
        },
        guests: {
            type: Object,
            required: true,
        },
        allowUpdate: {
            type: Boolean,
            default: true,
        },
        value: {
            type: Object,
            default: null,
        },
        showFilter: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        searchTitle: {
            type: String,
            default() {
                return this.$t('searchTitlePlaceholder');
            },
        },
    },
    computed: {
        ...mapGetters({
            appLocale: 'appState/getLocale',
            redeemPointsToggle: 'appState/getRedeemPointsToggle',
            userIsAuth: 'user/isAuth',
            minimumRedeemPoints: 'appTenant/minimumRedemptionPoints',
        }),
        hotelReservationQuantity() {
            const { roomsCount, guests } = this;
            const { adultsCount, childrenCount } = guests;

            return `${this.$t('_common:terms.roomWithCount', { count: roomsCount })}, ${this.$t('_common:terms.guestWithCount', { count: adultsCount + childrenCount })}`.toLowerCase();
        },
        date() {
            const format = {
                full: dateConstants.FORMAT.FULL,
                period: dateConstants.FORMAT.PERIOD,
                periodFull: dateConstants.FORMAT.PERIOD_FULL,
                time: dateConstants.FORMAT.TIME,
            };
            return datePeriod(this.startDateTime, this.endDateTime, format, this.appLocale, false);
        },
        formattedPoints() {
            return this.$options.filters.bonusPoints(this.minimumRedeemPoints);
        },
    },
    methods: {
        ...mapActions({
            updateRedeemPointsToggle: 'appState/updateRedeemPointsToggle',
        }),
    },
    created() {
        if (!this.userIsAuth) {
            this.updateRedeemPointsToggle(false);
        }
    },
};
</script>

<style lang="scss">
@import '@scssVariables';
@import '@scssMixins';

.gst-add-hotel-reservation-header {
    line-height: line-height('xxxxl');
    background-color: theme-color('white');
    overflow-y: auto;

    .gst-add-hotel-reservation-header__title {
        padding-left: theme-spacing(4);
        color: theme-color('quaternary');
        font-size: font-size('xxl');
        font-weight: font-weight('large');
    }

    .gst-add-hotel-reservation-header__content {
        background-color: theme-color('quinary') !important;
        cursor: pointer;

        .gst-add-hotel-reservation-header__wrapper {
            align-items: center;

            .gst-add-hotel-reservation-header__location-title {
                padding: 0 !important;
                color: theme-color('quaternary');
                font-size: font-size('l');
                font-weight: font-weight('large');
            }
        }

        .gst-add-hotel-reservation-header__icon {
            height: unset;
        }
    }

    .gst-add-hotel-reservation-header__content--disabled-filter {
        background-color: theme-color-hex('primary-lighten-1');
        cursor: default;

        .gst-base-button {
            border: none;
            color: theme-color('tertiary') !important;
        }

        .calendar-icon {
            .gst-svg-icon {
                fill: theme-color('tertiary');
            }
        }

        .gst-add-hotel-reservation-header__wrapper
            .gst-add-hotel-reservation-header__location-title,
        .gst-add-hotel-reservation-header__location-details {
            color: theme-color('tertiary');
        }

        .gst-base-icon {
            path {
                fill: theme-color('tertiary');
            }
        }
    }

    .gst-base-button {
        padding: theme-spacing(4) !important;
        border: 1px solid theme-color('primary');
        margin: theme-spacing(1, 2, 1, 0);
        background-color: theme-color('white');
        color: theme-color('primary');
        font-size: font-size('xs');
        letter-spacing: normal;
        text-transform: none;
        border-radius: 2px;
    }

    .calendar-icon {
        .gst-svg-icon {
            fill: theme-color('primary');
        }
    }

    .calendar-icon--read-only {
        .gst-svg-icon {
            fill: theme-color('quaternary');
        }
    }

    .dot {
        height: 4px;
        width: 4px;
        margin: 0 8px;
        background-color: theme-color('quaternary');
        border-radius: 50%;
    }

    .gst-add-hotel-reservation-header__redeem {
        gap: theme-spacing(2);

        .gst-add-hotel-reservation-header__redeem-label {
            color: theme-color('tertiary');
            font-size: font-size('s');
            font-weight: font-weight('regular');
            font-family: 'Roboto', sans-serif;
            letter-spacing: -0.25px;
        }

        .gst-add-hotel-reservation-header__redeem-switch {
            ::v-deep .v-messages {
                min-height: 0 !important;
            }

            &.gst-switch.v-input--switch {
                .v-input--selection-controls__input {
                    margin-right: 0;
                }

                .v-input--switch__track {
                    background-color: theme-color('switch-off') !important;
                    opacity: 1;
                }

                &.v-input--is-label-active {
                    .v-input--switch__track {
                        background-color: theme-color('primary') !important;
                    }
                }
            }
        }
    }
}

.gst-add-hotel-reservation-header.gst-add-hotel-reservation-header--show-filter {
    position: relative;
    z-index: $z-index-event-page-over-layer + 3;
}

@include mobile-only {
    .gst-add-hotel-reservation-header {
        .gst-base-button {
            padding: theme-spacing(4, 2) !important;
            border: 1px solid theme-color('white');
            font-size: font-size('xxs');
        }

        .gst-add-hotel-reservation-header__content {
            .gst-add-hotel-reservation-header__wrapper {
                .gst-add-hotel-reservation-header__location-title {
                    font-size: font-size('xs');
                }
            }

            .gst-add-hotel-reservation-header__location-details {
                font-size: font-size('xxs');
            }
        }
    }
}
</style>
